import * as THREE from 'three';
import { TILE } from '../../shared/geo.js';
import { hash2 } from '../../shared/rng.js';
import { WALL_CELL, WALL_COLS, WALL_ROWS } from './textures.js';

// Every building of a tile from its map footprint, by rule (the city grammar skill and
// docs/ARCHITECTURE.md section 4): walls split into storeys and bays, a door on the side that faces
// the road, a plinth and a damp foot, sills under the windows, a zinc roof with a wide overhang and a
// fascia, a veranda on shops and some houses, a signboard on shops, and a walled compound with a
// gate around houses that have the room. The same function builds whole tiles and 128 m cells.

const lin = (hex) => new THREE.Color(hex);

const HOUSE_WALLS = ['#e9dcc0', '#f1eee6', '#ecd58c', '#cfe0ea', '#e7c4b0', '#cfe3cc', '#d8b08a', '#f1eee6', '#e9dcc0'].map(lin);
const BARE_WALLS = ['#b9785a', '#bdbab4', '#a86a4e', '#c9c6c0'].map(lin); // mud brick and unplastered block
const SHOP_WALLS = ['#e8e2d2', '#d9c9a0', '#c8d6de', '#e3b9a0', '#f0f0ee', '#e0d070'].map(lin);
const KIND_WALL = { 2: lin('#e6d9a8'), 3: lin('#ede6d6'), 4: lin('#e8efe6'), 5: lin('#eef2f2'), 6: lin('#e8dcc0'), 7: lin('#c8c8c4'), 9: lin('#f0e4cc'), 10: lin('#e2dcc8'), 11: lin('#d6cdb8'), 12: lin('#dfe3e6') };
const ROOFS = ['#c9ced2', '#a9aeb2', '#b4b9bc', '#9a6a4c', '#8a5a40', '#7c4a36', '#8e3b30', '#3f6f9e', '#3d7a52', '#b8a890'].map(lin);
const SIGNS = ['#f2b705', '#c8402f', '#2f6fb4', '#2f8a56', '#e07a28', '#f4f1e8'].map(lin); // fictional shop-front paint
const GATES = ['#5a3a3a', '#2f4fa0', '#3a3a3c', '#8e3b30', '#2f8a56'].map(lin);
const FOUNDATION = lin('#8d8a86');
const PLINTH = lin('#8f6a58'); // concrete under red dust
const SILL = lin('#d9d4ca');
const TIMBER = lin('#7a6a56');
const POST = lin('#b9b4ab');
const COMPOUND_WALLS = ['#a09a92', '#b9b4ab', '#e6dcc4', '#a86a48'].map(lin);
const STOREY = 3.1;

// BIM block: the dimensions every rule below is built from (metres)
const SILL_H = 0.9;
const PLINTH_H = 0.45;
const VERANDA_MIN = 1.5;
const VERANDA_MAX = 2.0;
const EAVE = 0.6;
const FASCIA = 0.2;
const WALL_H = 1.9;
const WALL_T = 0.2;
const GATE_W = 3.0;
const SIGN_H = 0.7;

function cellRect(cell) {
  const col = cell % WALL_COLS;
  const row = Math.floor(cell / WALL_COLS);
  const pad = 0.012;
  return [(col + pad) / WALL_COLS, 1 - (row + 1 - pad) / WALL_ROWS, (col + 1 - pad) / WALL_COLS, 1 - (row + pad) / WALL_ROWS];
}
const RECT = Object.fromEntries(Object.entries(WALL_CELL).map(([name, cell]) => [name, cellRect(cell)]));
export const SOLID = [RECT.plain[0] + 0.02, RECT.plain[1] + 0.02, RECT.plain[0] + 0.05, RECT.plain[1] + 0.05]; // a plain speck, for trim
// the glass of the window cell: anything drawn with it (and lit > the night cut) glows after dark
export const GLASS = [RECT.window[0] + 0.34, RECT.window[1] + 0.3, RECT.window[0] + 0.66, RECT.window[1] + 0.6];

const shade = (c, k) => new THREE.Color(c.r * k, c.g * k, c.b * k);

// ---------------------------------------------------------------------------------------------
// Where a building stands

// Its corners in tile-local metres, the ground under each, and the level its floor is set at
// (halfway up the slope it sits on).
function stance(b, tx, tz, heightmap) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const n = b.p.length / 2;
  const xs = new Array(n);
  const zs = new Array(n);
  const ground = new Array(n);
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i++) {
    xs[i] = b.p[i * 2] / 10;
    zs[i] = b.p[i * 2 + 1] / 10;
    ground[i] = heightmap.heightAt(ox + xs[i], oz + zs[i]);
    if (ground[i] < lo) lo = ground[i];
    if (ground[i] > hi) hi = ground[i];
  }
  return { n, xs, zs, ground, floor: lo + (hi - lo) * 0.5 + 0.15 };
}

export const stanceOf = stance;

// The wall and roof colours a building gets, from the same seeded choices the full geometry makes.
export function buildingColours(b, index, tx, tz) {
  const seed = tx * 7919 + tz * 104729 + index * 31;
  const r1 = hash2(seed, 1, 3);
  const r2 = hash2(seed, 2, 3);
  const r3 = hash2(seed, 3, 3);
  const kind = b.k;
  const bare = kind === 8 || (kind === 0 && r1 < 0.24);
  let wall;
  if (bare) wall = BARE_WALLS[Math.floor(r2 * BARE_WALLS.length)];
  else if (kind === 0) wall = HOUSE_WALLS[Math.floor(r2 * HOUSE_WALLS.length)];
  else if (kind === 1) wall = SHOP_WALLS[Math.floor(r2 * SHOP_WALLS.length)];
  else wall = KIND_WALL[kind] || HOUSE_WALLS[0];
  const roof = kind === 4 ? ROOFS[8] : ROOFS[Math.floor(r3 * ROOFS.length)];
  return { wall, roof };
}

// A building's footprint in world coordinates, for collision: the same object the geometry
// builder returns, without building any geometry.
export function footprint(b, tx, tz, heightmap) {
  const { n, xs, zs, floor } = stance(b, tx, tz, heightmap);
  return collider(b, n, xs, zs, floor + b.h, tx, tz);
}

function collider(b, n, xs, zs, eave, tx, tz) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const world = new Float32Array(n * 2);
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = ox + xs[i];
    const z = oz + zs[i];
    world[i * 2] = x;
    world[i * 2 + 1] = z;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return { p: world, minX, minZ, maxX, maxZ, top: eave, name: b.n || '', kind: b.k };
}

// ---------------------------------------------------------------------------------------------
// The tile around a building: roads to face, neighbours to keep clear of

const plans = new WeakMap(); // tile data -> { segs, grid, rects, compounds }
const GRID = 32; // metres

// What the grammar needs to know about a whole tile, worked out once per tile data object.
function planTile(data) {
  let plan = plans.get(data);
  if (plan) return plan;
  const segs = [];
  for (const r of data.r) {
    const half = r.w / 2;
    for (let i = 2; i < r.p.length; i += 2) segs.push({ ax: r.p[i - 2] / 10, az: r.p[i - 1] / 10, bx: r.p[i] / 10, bz: r.p[i + 1] / 10, half });
  }
  const rects = data.b.map((b) => {
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < b.p.length; i += 2) {
      const x = b.p[i] / 10;
      const z = b.p[i + 1] / 10;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (z < z0) z0 = z;
      if (z > z1) z1 = z;
    }
    return { x0, z0, x1, z1 };
  });
  const grid = new Map();
  rects.forEach((r, i) => {
    for (let gz = Math.floor(r.z0 / GRID); gz <= Math.floor(r.z1 / GRID); gz++) {
      for (let gx = Math.floor(r.x0 / GRID); gx <= Math.floor(r.x1 / GRID); gx++) {
        const k = gx * 100003 + gz;
        let list = grid.get(k);
        if (!list) grid.set(k, (list = []));
        list.push(i);
      }
    }
  });
  plan = { segs, grid, rects, compounds: new Map() };
  plans.set(data, plan);
  return plan;
}

// The nearest point of any road to (x, z), tile-local metres: { d, px, pz, half } or null.
function nearestRoad(x, z, segs) {
  let best = null;
  let bestD = Infinity;
  for (const s of segs) {
    const ex = s.bx - s.ax;
    const ez = s.bz - s.az;
    let u = ((x - s.ax) * ex + (z - s.az) * ez) / (ex * ex + ez * ez || 1);
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const px = s.ax + ex * u;
    const pz = s.az + ez * u;
    const d = Math.hypot(x - px, z - pz) - s.half;
    if (d < bestD) {
      bestD = d;
      best = { d, px, pz, half: s.half };
    }
  }
  return best;
}

// True if a road runs through the rectangle (grown by pad).
function roadCrosses(x0, z0, x1, z1, segs, pad) {
  for (const s of segs) {
    const p = s.half + pad;
    const lx0 = Math.min(s.ax, s.bx) - p;
    const lx1 = Math.max(s.ax, s.bx) + p;
    const lz0 = Math.min(s.az, s.bz) - p;
    const lz1 = Math.max(s.az, s.bz) + p;
    if (lx1 < x0 || lx0 > x1 || lz1 < z0 || lz0 > z1) continue;
    const len = Math.hypot(s.bx - s.ax, s.bz - s.az);
    const steps = Math.max(1, Math.ceil(len));
    for (let k = 0; k <= steps; k++) {
      const x = s.ax + ((s.bx - s.ax) * k) / steps;
      const z = s.az + ((s.bz - s.az) * k) / steps;
      if (x > x0 - p && x < x1 + p && z > z0 - p && z < z1 + p) return true;
    }
  }
  return false;
}

// True if another building's box touches the rectangle.
function neighbourIn(x0, z0, x1, z1, self, plan) {
  for (let gz = Math.floor(z0 / GRID); gz <= Math.floor(z1 / GRID); gz++) {
    for (let gx = Math.floor(x0 / GRID); gx <= Math.floor(x1 / GRID); gx++) {
      const list = plan.grid.get(gx * 100003 + gz);
      if (!list) continue;
      for (const i of list) {
        if (i === self) continue;
        const r = plan.rects[i];
        if (r.x1 > x0 && r.x0 < x1 && r.z1 > z0 && r.z0 < z1) return true;
      }
    }
  }
  return false;
}

// The walled compound of house `index`, or null: a rectangle (tile-local metres) around the house
// with a gate on the side nearest the road, where no road or neighbour is in the way.
export function compoundOf(data, index, tx, tz) {
  const plan = planTile(data);
  if (plan.compounds.has(index)) return plan.compounds.get(index);
  const b = data.b[index];
  let result = null;
  if (b.k === 0 && b.p.length >= 6) {
    const seed = tx * 7919 + tz * 104729 + index * 31;
    const r = plan.rects[index];
    const area = (r.x1 - r.x0) * (r.z1 - r.z0);
    if (hash2(seed, 6, 3) < 0.5 && area > 30 && area < 420) {
      const [cx, cz] = [(r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2];
      const road = nearestRoad(cx, cz, plan.segs);
      for (const margin of [4.5, 3, 2]) {
        const x0 = r.x0 - margin;
        const z0 = r.z0 - margin;
        const x1 = r.x1 + margin;
        const z1 = r.z1 + margin;
        if (x0 < 0.5 || z0 < 0.5 || x1 > TILE - 0.5 || z1 > TILE - 0.5) continue; // whole, inside the tile
        if (roadCrosses(x0, z0, x1, z1, plan.segs, 1)) continue;
        if (neighbourIn(x0 - 0.5, z0 - 0.5, x1 + 0.5, z1 + 0.5, index, plan)) continue;
        // the gate: on the side facing the road, where the road is nearest
        let side = 0; // 0 north (z0), 1 east (x1), 2 south (z1), 3 west (x0)
        let gateAt = (x0 + x1) / 2;
        if (road) {
          const dn = Math.abs(road.pz - z0);
          const ds = Math.abs(road.pz - z1);
          const dw = Math.abs(road.px - x0);
          const de = Math.abs(road.px - x1);
          const m = Math.min(dn, ds, dw, de);
          side = m === dn ? 0 : m === ds ? 2 : m === dw ? 3 : 1;
          gateAt = side === 0 || side === 2 ? Math.max(x0 + GATE_W, Math.min(x1 - GATE_W, road.px)) : Math.max(z0 + GATE_W, Math.min(z1 - GATE_W, road.pz));
        }
        result = { x0, z0, x1, z1, side, gateAt, height: WALL_H + hash2(seed, 7, 3) * 0.3, colour: Math.floor(hash2(seed, 8, 3) * COMPOUND_WALLS.length), gate: Math.floor(hash2(seed, 9, 3) * GATES.length) };
        break;
      }
    }
  }
  plan.compounds.set(index, result);
  return result;
}

// The pieces of a compound's wall as collision footprints in world metres: each side split at
// the gate.
export function compoundColliders(c, tx, tz) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const out = [];
  const piece = (x0, z0, x1, z1) => {
    if (x1 - x0 < 0.05 || z1 - z0 < 0.05) return;
    out.push({ p: new Float32Array([ox + x0, oz + z0, ox + x1, oz + z0, ox + x1, oz + z1, ox + x0, oz + z1]), minX: ox + x0, minZ: oz + z0, maxX: ox + x1, maxZ: oz + z1, top: Infinity, name: '', kind: -1 });
  };
  const t = WALL_T;
  const g = GATE_W / 2;
  // north and south run the full width; east and west sit between them
  if (c.side === 0) {
    piece(c.x0, c.z0, c.gateAt - g, c.z0 + t);
    piece(c.gateAt + g, c.z0, c.x1, c.z0 + t);
  } else piece(c.x0, c.z0, c.x1, c.z0 + t);
  if (c.side === 2) {
    piece(c.x0, c.z1 - t, c.gateAt - g, c.z1);
    piece(c.gateAt + g, c.z1 - t, c.x1, c.z1);
  } else piece(c.x0, c.z1 - t, c.x1, c.z1);
  if (c.side === 3) {
    piece(c.x0, c.z0 + t, c.x0 + t, c.gateAt - g);
    piece(c.x0, c.gateAt + g, c.x0 + t, c.z1 - t);
  } else piece(c.x0, c.z0 + t, c.x0 + t, c.z1 - t);
  if (c.side === 1) {
    piece(c.x1 - t, c.z0 + t, c.x1, c.gateAt - g);
    piece(c.x1 - t, c.gateAt + g, c.x1, c.z1 - t);
  } else piece(c.x1 - t, c.z0 + t, c.x1, c.z1 - t);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Pieces of geometry

// A box along the ground line a→b, from height y0 to y1, standing `depth` out along the outward
// normal (nx, nz) from the line: its front and top, and the ends and underside only when asked
// (a sill or plinth is never seen from below or end-on at any distance that matters).
function slab(builder, ax, az, bx, bz, y0, y1, nx, nz, depth, colour, lowColour = colour, ends = false) {
  const ox = nx * depth;
  const oz = nz * depth;
  const A0 = [ax, y0, az];
  const B0 = [bx, y0, bz];
  const A1 = [ax, y1, az];
  const B1 = [bx, y1, bz];
  const a0 = [ax + ox, y0, az + oz];
  const b0 = [bx + ox, y0, bz + oz];
  const a1 = [ax + ox, y1, az + oz];
  const b1 = [bx + ox, y1, bz + oz];
  builder.quad2(a0, b0, b1, a1, lowColour, colour, SOLID); // front
  builder.quad(a1, b1, B1, A1, colour, SOLID); // top
  if (!ends) return;
  builder.quad(A0, B0, b0, a0, lowColour, SOLID); // underside
  builder.quad(A0, a0, a1, A1, colour, SOLID); // end at a
  builder.quad(b0, B0, B1, b1, colour, SOLID); // end at b
}

// A square post from the ground up.
function post(builder, x, y0, z, y1, half, colour) {
  const c = [
    [x - half, z - half],
    [x + half, z - half],
    [x + half, z + half],
    [x - half, z + half],
  ];
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    builder.quad([c[i][0], y0, c[i][1]], [c[j][0], y0, c[j][1]], [c[j][0], y1, c[j][1]], [c[i][0], y1, c[i][1]], colour, SOLID);
  }
}

// Pushes a triangle whichever way round makes its normal point upward.
function upTri(builder, a, b, c, color, ua, ub, uc) {
  const ny = (b[2] - a[2]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[2] - a[2]);
  if (ny >= 0) builder.tri(a, b, c, color, ua, ub, uc);
  else builder.tri(a, c, b, color, ua, uc, ub);
}

// ---------------------------------------------------------------------------------------------
// One building

function addBuilding(b, index, tx, tz, heightmap, walls, roofs, data) {
  const { n, xs, zs, ground, floor } = stance(b, tx, tz, heightmap);
  const ox = tx * TILE;
  const oz = tz * TILE;
  const plan = planTile(data);
  const seed = tx * 7919 + tz * 104729 + index * 31;
  const r1 = hash2(seed, 1, 3);
  const r2 = hash2(seed, 2, 3);
  const r3 = hash2(seed, 3, 3);
  const awake = hash2(seed, 4, 3); // how late this household keeps its lights on
  const kind = b.k;
  const storeys = kind === 3 || kind === 4 || kind === 7 || kind === 8 || kind === 11 ? 1 : Math.max(1, Math.round(b.h / STOREY));
  const storeyH = b.h / storeys;
  const eave = floor + b.h;

  const bare = kind === 8 || (kind === 0 && r1 < 0.24);
  let wall;
  if (bare) wall = BARE_WALLS[Math.floor(r2 * BARE_WALLS.length)];
  else if (kind === 0) wall = HOUSE_WALLS[Math.floor(r2 * HOUSE_WALLS.length)];
  else if (kind === 1) wall = SHOP_WALLS[Math.floor(r2 * SHOP_WALLS.length)];
  else wall = KIND_WALL[kind] || HOUSE_WALLS[0];
  const roofColor = kind === 4 ? ROOFS[8] : ROOFS[Math.floor(r3 * ROOFS.length)];
  const foot = shade(wall, 0.78); // the damp, dusty foot of a ground-floor wall
  const upper = shade(wall, 0.94);

  // the middle of the footprint, to know which way is out
  let cx = 0;
  let cz = 0;
  for (let i = 0; i < n; i++) {
    cx += xs[i];
    cz += zs[i];
  }
  cx /= n;
  cz /= n;
  const outward = (i) => {
    const j = (i + 1) % n;
    const dx = xs[j] - xs[i];
    const dz = zs[j] - zs[i];
    const len = Math.hypot(dx, dz) || 1;
    let nx = dz / len;
    let nz = -dx / len;
    const mx = (xs[i] + xs[j]) / 2 - cx;
    const mz = (zs[i] + zs[j]) / 2 - cz;
    if (nx * mx + nz * mz < 0) {
      nx = -nx;
      nz = -nz;
    }
    return [nx, nz, len];
  };

  // the longest wall, and the wall that faces the nearest road: the door and the veranda go there
  let longest = 0;
  let longestLen = 0;
  for (let i = 0; i < n; i++) {
    const [, , len] = outward(i);
    if (len > longestLen) {
      longestLen = len;
      longest = i;
    }
  }
  const road = nearestRoad(cx, cz, plan.segs);
  let facing = longest;
  if (road && road.d < 40) {
    let bestDot = -Infinity;
    for (let i = 0; i < n; i++) {
      const [nx, nz, len] = outward(i);
      if (len < 2.4) continue;
      const j = (i + 1) % n;
      const mx = (xs[i] + xs[j]) / 2;
      const mz = (zs[i] + zs[j]) / 2;
      const tx2 = road.px - mx;
      const tz2 = road.pz - mz;
      const dot = (nx * tx2 + nz * tz2) / (Math.hypot(tx2, tz2) || 1) + len * 0.01;
      if (dot > bestDot) {
        bestDot = dot;
        facing = i;
      }
    }
  }

  const compound = compoundOf(data, index, tx, tz);
  const signColour = SIGNS[Math.floor(hash2(seed, 11, 3) * SIGNS.length)];
  // a veranda along the road side: every shop, some houses and schools, where there is room before the road
  const wantVeranda = kind === 1 || (kind === 0 && hash2(seed, 5, 3) < 0.35) || kind === 2 || kind === 10;
  const veranda = wantVeranda && road && longestLen > 3 && outward(facing)[2] > 3 && road.d - 1.2 >= VERANDA_MIN;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const [nx, nz, len] = outward(i);
    if (len < 0.3) continue;
    const bays = len < 1.9 ? 1 : Math.max(1, Math.round(len / 3.2));
    // foundation down to the lower ground, and a plinth standing proud of the wall
    const base = Math.min(ground[i], ground[j]) - 0.5;
    if (floor - base > 0.05) {
      walls.lit = 0;
      walls.quad([xs[i], base, zs[i]], [xs[j], base, zs[j]], [xs[j], floor, zs[j]], [xs[i], floor, zs[i]], FOUNDATION, RECT.block);
      if (kind !== 8 && len > 1.9) slab(walls, xs[i], zs[i], xs[j], zs[j], Math.max(base, floor - PLINTH_H), floor, nx, nz, 0.06, PLINTH, shade(PLINTH, 0.85));
    }
    for (let s = 0; s < storeys; s++) {
      const y0 = floor + s * storeyH;
      const y1 = y0 + storeyH;
      for (let k = 0; k < bays; k++) {
        const t0 = k / bays;
        const t1 = (k + 1) / bays;
        const ax = xs[i] + (xs[j] - xs[i]) * t0;
        const az = zs[i] + (zs[j] - zs[i]) * t0;
        const bx = xs[i] + (xs[j] - xs[i]) * t1;
        const bz = zs[i] + (zs[j] - zs[i]) * t1;
        const pick = hash2(seed + i * 13 + k, s, 9);
        let rect;
        let window = false;
        if (len < 1.9 || kind === 8) rect = bare ? RECT.block : RECT.plain;
        else if (kind === 3 || kind === 4) rect = k % 2 === 0 ? RECT.church : RECT.plain;
        else if (kind === 1 && s === 0) rect = pick < 0.85 ? RECT.shop : RECT.plain;
        else if (kind === 2 || kind === 10) rect = RECT.school;
        else if (kind === 7 || kind === 11) rect = pick < 0.3 ? RECT.window : RECT.plain;
        else if (s === 0 && i === facing && k === Math.floor(bays / 2)) rect = RECT.door;
        else if (pick < 0.78) {
          rect = bare ? RECT.blockWindow : RECT.window;
          window = true;
        } else rect = bare ? RECT.block : RECT.plain;
        // a number from 0 to 1 for the window: the higher, the later into the night it
        // stays lit. A house is mostly up or mostly asleep; schools are dark at night.
        walls.lit = kind === 2 || kind === 10 ? 0 : 0.05 + (awake * 0.7 + hash2(seed + i * 13 + k, s, 10) * 0.3) * 0.95;
        if (s === 0) walls.quad2([ax, y0, az], [bx, y0, bz], [bx, y1, bz], [ax, y1, az], foot, wall, rect);
        else walls.quad2([ax, y0, az], [bx, y0, bz], [bx, y1, bz], [ax, y1, az], upper, wall, rect);
        if (window && len / bays > 2.2) {
          // a sill under the window, a little shorter than the bay
          walls.lit = 0;
          const w = (t1 - t0) * 0.22;
          const sx0 = xs[i] + (xs[j] - xs[i]) * (t0 + w);
          const sz0 = zs[i] + (zs[j] - zs[i]) * (t0 + w);
          const sx1 = xs[i] + (xs[j] - xs[i]) * (t1 - w);
          const sz1 = zs[i] + (zs[j] - zs[i]) * (t1 - w);
          slab(walls, sx0, sz0, sx1, sz1, y0 + SILL_H - 0.06, y0 + SILL_H, nx, nz, 0.1, SILL);
        }
      }
    }
    // a shop's signboard runs the length of its front, above the shutters (or on the veranda's
    // front board when it has one, below)
    if (kind === 1 && i === facing && len > 2.4 && !veranda) {
      walls.lit = 0;
      const inset = 0.2 / len;
      const sx0 = xs[i] + (xs[j] - xs[i]) * inset;
      const sz0 = zs[i] + (zs[j] - zs[i]) * inset;
      const sx1 = xs[i] + (xs[j] - xs[i]) * (1 - inset);
      const sz1 = zs[i] + (zs[j] - zs[i]) * (1 - inset);
      const top = Math.min(floor + storeyH - 0.35, floor + 2.75);
      slab(walls, sx0, sz0, sx1, sz1, top - SIGN_H, top, nx, nz, 0.08, signColour, undefined, true);
    }
  }

  if (veranda) {
    const [nx, nz, len] = outward(facing);
    const room = road.d - 1.2;
    const depth = VERANDA_MIN + hash2(seed, 12, 3) * (VERANDA_MAX - VERANDA_MIN);
    {
      const d = Math.min(depth, room);
      const i = facing;
      const j = (i + 1) % n;
      const yIn = Math.min(eave - 0.05, floor + 3.0);
      const yOut = yIn - 0.45;
      const A = [xs[i], yIn, zs[i]];
      const B = [xs[j], yIn, zs[j]];
      const a = [xs[i] + nx * d, yOut, zs[i] + nz * d];
      const bb = [xs[j] + nx * d, yOut, zs[j] + nz * d];
      upTri(roofs, A, B, bb, roofColor, [0, 0], [len * 0.35, 0], [len * 0.35, d * 0.35]);
      upTri(roofs, A, bb, a, roofColor, [0, 0], [len * 0.35, d * 0.35], [0, d * 0.35]);
      // the underside, seen from the veranda
      roofs.tri(A, bb, B, shade(roofColor, 0.7), [0, 0], [len * 0.35, d * 0.35], [len * 0.35, 0]);
      roofs.tri(A, a, bb, shade(roofColor, 0.7), [0, 0], [0, d * 0.35], [len * 0.35, d * 0.35]);
      walls.lit = 0;
      // the front board: a shop's signboard in its paint, a house's plain timber
      const board = kind === 1 ? SIGN_H : 0.15;
      walls.quad([a[0], yOut - board, a[2]], [bb[0], yOut - board, bb[2]], bb, a, kind === 1 ? signColour : TIMBER, SOLID);
      const posts = Math.max(2, Math.ceil(len / 3.4) + 1);
      for (let k = 0; k < posts; k++) {
        const t = 0.08 + (0.84 * k) / (posts - 1);
        const px = xs[i] + (xs[j] - xs[i]) * t + nx * (d - 0.1);
        const pz = zs[i] + (zs[j] - zs[i]) * t + nz * (d - 0.1);
        const gy = heightmap.heightAt(ox + px, oz + pz);
        post(walls, px, Math.min(gy, floor) - 0.2, pz, yOut, 0.07, kind === 1 ? POST : TIMBER);
      }
    }
  }

  addRoof(xs, zs, n, eave, kind, r1, wall, roofColor, walls, roofs, bare);

  if (compound) addCompound(compound, tx, tz, heightmap, walls);

  return collider(b, n, xs, zs, eave, tx, tz);
}

// The wall round a compound, following the ground, with a gap and a steel gate on the road side.
function addCompound(c, tx, tz, heightmap, walls) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const colour = COMPOUND_WALLS[c.colour];
  const low = shade(colour, 0.8);
  const gate = GATES[c.gate];
  const g = GATE_W / 2;
  walls.lit = 0;
  // one side at a time: from (ax, az) to (bx, bz) with its outward normal, split at the gate
  const side = (ax, az, bx, bz, nx, nz, gateAt) => {
    const runs = [];
    if (gateAt === null) runs.push([ax, az, bx, bz]);
    else if (ax === bx) {
      const lo = Math.min(az, bz);
      const hi = Math.max(az, bz);
      runs.push([ax, lo, ax, gateAt - g], [ax, gateAt + g, ax, hi]);
    } else {
      const lo = Math.min(ax, bx);
      const hi = Math.max(ax, bx);
      runs.push([lo, az, gateAt - g, az], [gateAt + g, az, hi, az]);
    }
    for (const [x0, z0, x1, z1] of runs) {
      const len = Math.hypot(x1 - x0, z1 - z0);
      if (len < 0.3) continue;
      const parts = Math.max(1, Math.ceil(len / 12));
      for (let k = 0; k < parts; k++) {
        const t0 = k / parts;
        const t1 = (k + 1) / parts;
        const px0 = x0 + (x1 - x0) * t0;
        const pz0 = z0 + (z1 - z0) * t0;
        const px1 = x0 + (x1 - x0) * t1;
        const pz1 = z0 + (z1 - z0) * t1;
        const g0 = heightmap.heightAt(ox + px0, oz + pz0);
        const g1 = heightmap.heightAt(ox + px1, oz + pz1);
        const y0 = Math.min(g0, g1) - 0.3;
        const y1 = Math.max(g0, g1) + c.height;
        // outer face, inner face and the top; the thickness goes inward from the line
        const ix = -nx * WALL_T;
        const iz = -nz * WALL_T;
        walls.quad2([px0, y0, pz0], [px1, y0, pz1], [px1, y1, pz1], [px0, y1, pz0], low, colour, RECT.plain);
        walls.quad2([px1 + ix, y0, pz1 + iz], [px0 + ix, y0, pz0 + iz], [px0 + ix, y1, pz0 + iz], [px1 + ix, y1, pz1 + iz], low, colour, RECT.plain);
        walls.quad([px0, y1, pz0], [px1, y1, pz1], [px1 + ix, y1, pz1 + iz], [px0 + ix, y1, pz0 + iz], colour, SOLID);
      }
    }
    if (gateAt !== null) {
      // gate posts and the closed steel gate between them
      const gx0 = ax === bx ? ax : gateAt - g;
      const gz0 = ax === bx ? gateAt - g : az;
      const gx1 = ax === bx ? ax : gateAt + g;
      const gz1 = ax === bx ? gateAt + g : az;
      const gy = Math.max(heightmap.heightAt(ox + gx0, oz + gz0), heightmap.heightAt(ox + gx1, oz + gz1));
      post(walls, gx0 - nx * WALL_T * 0.5, gy - 0.3, gz0 - nz * WALL_T * 0.5, gy + c.height + 0.25, 0.18, colour);
      post(walls, gx1 - nx * WALL_T * 0.5, gy - 0.3, gz1 - nz * WALL_T * 0.5, gy + c.height + 0.25, 0.18, colour);
      const off = -nx * WALL_T * 0.5;
      const offz = -nz * WALL_T * 0.5;
      walls.quad([gx0 + off, gy - 0.1, gz0 + offz], [gx1 + off, gy - 0.1, gz1 + offz], [gx1 + off, gy + c.height - 0.1, gz1 + offz], [gx0 + off, gy + c.height - 0.1, gz0 + offz], gate, SOLID);
      walls.quad([gx1 + off, gy - 0.1, gz1 + offz], [gx0 + off, gy - 0.1, gz0 + offz], [gx0 + off, gy + c.height - 0.1, gz0 + offz], [gx1 + off, gy + c.height - 0.1, gz1 + offz], shade(gate, 0.8), SOLID);
    }
  };
  // outward normals: north (z0) faces -z, east (x1) +x, south (z1) +z, west (x0) -x
  side(c.x0, c.z0, c.x1, c.z0, 0, -1, c.side === 0 ? c.gateAt : null);
  side(c.x1, c.z0, c.x1, c.z1, 1, 0, c.side === 1 ? c.gateAt : null);
  side(c.x1, c.z1, c.x0, c.z1, 0, 1, c.side === 2 ? c.gateAt : null);
  side(c.x0, c.z1, c.x0, c.z0, -1, 0, c.side === 3 ? c.gateAt : null);
}

function addRoof(xs, zs, n, eave, kind, r, wall, color, walls, roofs, bare) {
  // fit a box along the longest wall and see how well the footprint fills it
  let ux = 1;
  let uz = 0;
  let best = 0;
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = xs[j] - xs[i];
    const dz = zs[j] - zs[i];
    area += xs[i] * zs[j] - xs[j] * zs[i];
    const len = Math.hypot(dx, dz);
    if (len > best) {
      best = len;
      ux = dx / len;
      uz = dz / len;
    }
  }
  area = Math.abs(area) / 2;
  const vx = -uz;
  const vz = ux;
  let u0 = Infinity;
  let u1 = -Infinity;
  let v0 = Infinity;
  let v1 = -Infinity;
  for (let i = 0; i < n; i++) {
    const u = xs[i] * ux + zs[i] * uz;
    const v = xs[i] * vx + zs[i] * vz;
    if (u < u0) u0 = u;
    if (u > u1) u1 = u;
    if (v < v0) v0 = v;
    if (v > v1) v1 = v;
  }
  const boxArea = (u1 - u0) * (v1 - v0);
  const SCALE = 0.35; // zinc ridges per metre of roof

  if (n <= 10 && area / boxArea > 0.76) {
    const over = kind === 8 ? 0.15 : EAVE;
    u0 -= over;
    u1 += over;
    v0 -= over;
    v1 += over;
    // make u the long axis
    let ax = ux;
    let az = uz;
    let bx = vx;
    let bz = vz;
    let a0 = u0;
    let a1 = u1;
    let b0 = v0;
    let b1 = v1;
    if (v1 - v0 > u1 - u0) {
      ax = vx;
      az = vz;
      bx = ux;
      bz = uz;
      a0 = v0;
      a1 = v1;
      b0 = u0;
      b1 = u1;
    }
    const half = (b1 - b0) / 2;
    const rise = Math.min(3.4, half * (kind === 3 ? 0.75 : 0.42));
    const hip = kind !== 3 && r < 0.55;
    const inset = hip ? Math.min(half * 0.9, (a1 - a0) / 2 - 0.05) : 0;
    const bm = (b0 + b1) / 2;
    const P = (a, b, y) => [a * ax + b * bx, y, a * az + b * bz];
    const e00 = P(a0, b0, eave);
    const e10 = P(a1, b0, eave);
    const e11 = P(a1, b1, eave);
    const e01 = P(a0, b1, eave);
    const ra = P(a0 + inset, bm, eave + rise);
    const rb = P(a1 - inset, bm, eave + rise);
    const slope = Math.hypot(half, rise) * 0.5;
    // two long slopes; winding chosen so the faces look outward and up
    upTri(roofs, e00, ra, rb, color, [a0 * SCALE, 0], [(a0 + inset) * SCALE, slope], [(a1 - inset) * SCALE, slope]);
    upTri(roofs, e00, rb, e10, color, [a0 * SCALE, 0], [(a1 - inset) * SCALE, slope], [a1 * SCALE, 0]);
    upTri(roofs, e11, rb, ra, color, [a1 * SCALE, 0], [(a1 - inset) * SCALE, slope], [(a0 + inset) * SCALE, slope]);
    upTri(roofs, e11, ra, e01, color, [a1 * SCALE, 0], [(a0 + inset) * SCALE, slope], [a0 * SCALE, 0]);
    if (hip) {
      upTri(roofs, e01, ra, e00, color, [b1 * SCALE, 0], [bm * SCALE, slope], [b0 * SCALE, 0]);
      upTri(roofs, e10, rb, e11, color, [b0 * SCALE, 0], [bm * SCALE, slope], [b1 * SCALE, 0]);
    } else {
      // gable ends are wall
      const rect = bare ? RECT.block : RECT.plain;
      const mid = [(rect[0] + rect[2]) / 2, rect[3]];
      walls.lit = 0;
      upTri(walls, e01, ra, e00, wall, [rect[0], rect[1]], mid, [rect[2], rect[1]]);
      upTri(walls, e10, rb, e11, wall, [rect[0], rect[1]], mid, [rect[2], rect[1]]);
    }
    // the fascia board under the eave, all the way round (its outer face; the back is in the dark)
    if (kind !== 8 && a1 - a0 > 4) {
      walls.lit = 0;
      const corners = [e00, e10, e11, e01];
      const mx = (e00[0] + e11[0]) / 2;
      const mz = (e00[2] + e11[2]) / 2;
      for (let i = 0; i < 4; i++) {
        const p = corners[i];
        const q = corners[(i + 1) % 4];
        // wind the quad so its face points away from the middle of the roof
        const nx = q[2] - p[2];
        const nz = -(q[0] - p[0]);
        const out = nx * ((p[0] + q[0]) / 2 - mx) + nz * ((p[2] + q[2]) / 2 - mz) > 0;
        if (out) walls.quad([p[0], eave - FASCIA, p[2]], [q[0], eave - FASCIA, q[2]], [q[0], eave, q[2]], [p[0], eave, p[2]], TIMBER, SOLID);
        else walls.quad([q[0], eave - FASCIA, q[2]], [p[0], eave - FASCIA, p[2]], [p[0], eave, p[2]], [q[0], eave, q[2]], TIMBER, SOLID);
      }
    }
    return;
  }

  // irregular footprint: a flat sheet roof just above the walls
  const contour = [];
  for (let i = 0; i < n; i++) contour.push(new THREE.Vector2(xs[i], zs[i]));
  const faces = THREE.ShapeUtils.triangulateShape(contour, []);
  const y = eave + 0.3;
  for (const f of faces) {
    const a = [xs[f[0]], y, zs[f[0]]];
    const b = [xs[f[1]], y, zs[f[1]]];
    const c = [xs[f[2]], y, zs[f[2]]];
    upTri(roofs, a, b, c, color, [a[0] * SCALE, a[2] * SCALE], [b[0] * SCALE, b[2] * SCALE], [c[0] * SCALE, c[2] * SCALE]);
  }
  // a thin band closes the gap between wall top and sheet
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    roofs.quad([xs[i], eave, zs[i]], [xs[j], eave, zs[j]], [xs[j], y, zs[j]], [xs[i], y, zs[i]], color, [0, 0, 0.1, 0.1]);
  }
}

// Builds every building of a tile, or only those whose indices are listed. Returns the
// footprints for collision (the compound walls' pieces included).
// skip: indices of buildings a hand-made landmark model stands for (their footprints still count
// for collision, through buildingColliders).
export function addBuildings(data, tx, tz, heightmap, walls, roofs, indices = null, skip = null) {
  const colliders = [];
  const count = indices ? indices.length : data.b.length;
  for (let k = 0; k < count; k++) {
    const i = indices ? indices[k] : k;
    const b = data.b[i];
    if (b.p.length < 6) continue;
    if (skip && skip.has(i)) continue;
    colliders.push(addBuilding(b, i, tx, tz, heightmap, walls, roofs, data));
    const c = compoundOf(data, i, tx, tz);
    if (c) for (const piece of compoundColliders(c, tx, tz)) colliders.push(piece);
  }
  return colliders;
}
